import React from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import queryString from 'query-string'
import styled from 'styled-components/macro'
import Dropdown from 'react-bootstrap/Dropdown'
import palette from 'src/designSystem/variables/palette'
import {
  StyledDropdownMenu,
  StyledDropdownToggle,
} from '../StandardDropdown/StandardDropdown.styles'
import { FilterParam } from '../IndexPage/FilterBar/FilterBar.types'

const PageSizeDropdownWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
`

const PageSizeLabel = styled.span`
  white-space: nowrap;
  font-weight: 400;
  color: ${palette.neutral[400]};
`

const StyledPageSizeToggle = styled(StyledDropdownToggle)`
  font-size: 14px;
  padding: 2px 8px;
  min-width: 60px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 4px;
  background: transparent;

  &:hover {
    background: ${palette.neutral[50]};
  }
`

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

export type PageSizeDropdownProps = {
  defaultPageSize?: number
  pageSizeOptions?: number[]
}

export const PageSizeDropdown = ({
  defaultPageSize = 20,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
}: PageSizeDropdownProps): JSX.Element => {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const pcParam = searchParams.get('pc')
  const pcValue = pcParam ? Number(pcParam) : NaN
  const currentPageSize =
    pcValue > 0 && pageSizeOptions.includes(pcValue) ? pcValue : defaultPageSize

  const handleSelect = (newSize: number) => {
    const parsed = queryString.parse(location.search, {
      arrayFormat: 'bracket',
    })
    const newUrl = queryString.stringifyUrl(
      {
        url: location.pathname,
        query: {
          ...parsed,
          pc: newSize,
          [FilterParam.PAGE]: 1,
        },
      },
      { arrayFormat: 'bracket' },
    )
    navigate(newUrl)
  }

  return (
    <PageSizeDropdownWrapper>
      <PageSizeLabel>Rows per page</PageSizeLabel>
      <Dropdown drop="down">
        <Dropdown.Toggle
          as={StyledPageSizeToggle}
          id="page-size-dropdown-toggle"
          aria-labelledby="page-size-dropdown-label"
        >
          {currentPageSize}
        </Dropdown.Toggle>
        <Dropdown.Menu as={StyledDropdownMenu}>
          {pageSizeOptions.map((size) => (
            <Dropdown.Item
              key={size}
              eventKey={size.toString()}
              onClick={() => handleSelect(size)}
              active={currentPageSize === size}
            >
              {size}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </PageSizeDropdownWrapper>
  )
}
